import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {
  CAREGIVER_ONBOARDING_STEPS,
  CAREGIVER_ONBOARDING_STEP_HINTS,
  CAREGIVER_ONBOARDING_STEP_LABELS,
  CaregiverOnboardingProgress,
  CaregiverOnboardingStep,
  caregiverOnboardingService,
} from '../services/caregiverOnboardingService';
import { caregiverProfileService } from '../services/caregiverProfileService';
import CredentialDocumentUploader from './CredentialDocumentUploader';
import StateAgencyRegisterLink from './StateAgencyRegisterLink';
import ModuleAcknowledgmentGate from './ModuleAcknowledgmentGate';

// FEAT-045 — Caregiver Sign-Up Stepper (web).
// MVP-8.10 (caregiver sign-up flow with structured steps),
// MVP-8.11 (incomplete caregivers excluded from discovery — server-side
// policy; this UI is the path to flipping that bit), MVP-18.17 (§18
// cross-reference for the structured sign-up flow).
//
// Tone (§1.2 elderly-friendly + §18.22 warm completion):
//   - "Let's get you ready to help families" beats "Complete onboarding".
//   - The in-progress banner uses warm primary/tertiary tokens — never
//     red error styling.
//   - Final state celebrates with the same copy the warm completion
//     notification persists ("You're all set! Your profile is now
//     visible to the families who need you most.")
//
// Composition (no re-implementation):
//   - Profile Details: links to the existing FEAT-017 management surface.
//   - Credentials: composes FEAT-046 CredentialDocumentUploader + FEAT-047
//     StateAgencyRegisterLink. License number bound to
//     User.stateLicenseNumber via the existing PUT /caregiver-profile/profile
//     endpoint (FEAT-017).
//   - Learning: composes FEAT-046 ModuleAcknowledgmentGate per required
//     module. The "required modules" list is sourced from
//     REQUIRED_MODULE_IDS below — a constant placeholder until FEAT-046
//     surfaces a canonical required-module endpoint (documented in the
//     implementation doc).
//   - Platform Rules + Disclaimer: read-only copy provided by the work
//     item, gated behind a warm checkbox.

// Sourced from FEAT-039's REQUIRED_LEARNING_MODULE_COUNT (=1) — until the
// canonical required-module list lands in FEAT-046, the stepper requires
// the caregiver to acknowledge a single intro module. The list is
// surfaced here (rather than imported from the API) because the
// `/training/modules` endpoint does not currently expose a "required"
// flag. Documented in docs/features/FEAT-045-Implementation.md so the
// next FEAT-046 iteration can swap the source without UI churn.
const REQUIRED_MODULE_IDS = ['careva-foundations'];

const PLATFORM_RULES_TITLE = 'How we keep Careva warm';
const PLATFORM_RULES_BULLETS = [
  'Treat every family with patience and respect.',
  'Be honest about your availability and your training.',
  'Never ask families for money outside of Careva.',
  'Tell us right away if a situation feels unsafe.',
];
const PLATFORM_RULES_CONFIRM = "I've read these and I'll honor them.";

const DISCLAIMER_TITLE = 'Your independence';
const DISCLAIMER_BODY =
  "Careva connects you with families who are looking for care. You're an independent caregiver — not a Careva employee — and you set your own schedule. Careva isn't a legal employer and doesn't supervise the care you provide.";
const DISCLAIMER_CONFIRM = 'I understand and I agree.';

const COMPLETION_TITLE = "You're all set!";
const COMPLETION_BODY =
  'Your profile is now visible to the families who need you most.';

interface ProfileSnapshot {
  bio: string | null;
  avatar: string | null;
  stateCode: string | null;
  stateLicenseNumber: string | null;
}

interface CaregiverSignUpStepperProps {
  /** Where to navigate when the caregiver chooses "Save and continue later" or finishes. */
  onExit?: () => void;
}

const CaregiverSignUpStepper: React.FC<CaregiverSignUpStepperProps> = ({ onExit }) => {
  const [progress, setProgress] = useState<CaregiverOnboardingProgress | null>(null);
  const [profile, setProfile] = useState<ProfileSnapshot | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseInput, setLicenseInput] = useState('');
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [rulesChecked, setRulesChecked] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const refreshProgress = useCallback(async () => {
    const next = await caregiverOnboardingService.getProgress();
    setProgress(next);
    return next;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, prof] = await Promise.all([
          caregiverOnboardingService.getProgress(),
          caregiverProfileService.getProfile(),
        ]);
        if (cancelled) return;
        setProgress(p);
        setProfile({
          bio: prof.bio,
          avatar: prof.avatar,
          stateCode: prof.stateCode,
          stateLicenseNumber: prof.stateLicenseNumber,
        });
        setLicenseInput(prof.stateLicenseNumber ?? '');
        if (p.completedSteps.includes('platform_rules')) {
          setRulesChecked(true);
        }
        if (p.completedSteps.includes('disclaimer')) {
          setDisclaimerChecked(true);
        }
        const nextIdx = CAREGIVER_ONBOARDING_STEPS.findIndex(
          (s) => s === (p.nextStep ?? p.currentStep),
        );
        if (nextIdx >= 0) {
          setActiveStepIdx(nextIdx);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'We had trouble loading your sign-up progress. Please try again.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const completedSet = useMemo(
    () => new Set<CaregiverOnboardingStep>(progress?.completedSteps ?? []),
    [progress?.completedSteps],
  );

  const advance = useCallback(
    async (step: CaregiverOnboardingStep) => {
      setSubmitting(true);
      setError(null);
      try {
        const next = await caregiverOnboardingService.advance(step);
        setProgress(next);
        if (next.isComplete && !showCelebration) {
          setShowCelebration(true);
        } else if (next.nextStep) {
          const idx = CAREGIVER_ONBOARDING_STEPS.findIndex(
            (s) => s === next.nextStep,
          );
          if (idx >= 0) setActiveStepIdx(idx);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "We couldn't save that step right now. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [showCelebration],
  );

  const saveLicense = useCallback(async () => {
    const trimmed = licenseInput.trim();
    if (!trimmed) return;
    setLicenseSaving(true);
    try {
      // FEAT-045 reuses the existing FEAT-017 PUT
      // /caregiver-profile/profile route rather than introducing a
      // sibling endpoint. The route + Zod schema were extended in this
      // work item to accept stateLicenseNumber / stateCode (validation
      // regex shared with FEAT-047's verify-link substitution rule).
      await caregiverProfileService.updateProfile({
        stateLicenseNumber: trimmed,
      });
      setProfile((prev) =>
        prev ? { ...prev, stateLicenseNumber: trimmed } : prev,
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn't save your license number. Please try again.",
      );
    } finally {
      setLicenseSaving(false);
    }
  }, [licenseInput]);

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', py: 8 }}
        data-testid="caregiver-signup-stepper-loading"
      >
        <CircularProgress aria-label="Loading your sign-up progress" />
      </Box>
    );
  }

  if (!progress) {
    return (
      <Alert severity="info" data-testid="caregiver-signup-stepper-error">
        {error ??
          "We couldn't load your sign-up progress just yet. Please refresh."}
      </Alert>
    );
  }

  if (showCelebration || progress.isComplete) {
    return (
      <Card
        data-testid="caregiver-signup-stepper-complete"
        sx={{ borderLeft: '4px solid', borderColor: 'success.main' }}
      >
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <FavoriteIcon color="secondary" aria-hidden="true" />
            <Typography variant="h5" component="h2">
              {COMPLETION_TITLE}
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {COMPLETION_BODY}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onExit}
            data-testid="caregiver-signup-stepper-finish"
          >
            Back to my profile
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentStep = CAREGIVER_ONBOARDING_STEPS[activeStepIdx];

  return (
    <Box data-testid="caregiver-signup-stepper">
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <FavoriteIcon color="secondary" aria-hidden="true" />
            <Typography variant="h5" component="h1">
              Let's get you ready to help families
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Take it at your own pace — we'll save your progress so you can pick
            up wherever you left off, on any device.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress.completionPercent}
            aria-label={`Sign-up progress ${progress.completionPercent} percent`}
            data-testid="caregiver-signup-progress-bar"
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
            data-testid="caregiver-signup-progress-label"
          >
            {progress.completionPercent}% complete · {progress.completedSteps.length} of{' '}
            {CAREGIVER_ONBOARDING_STEPS.length} steps done
          </Typography>
        </CardContent>
      </Card>

      <Stepper
        activeStep={activeStepIdx}
        alternativeLabel
        sx={{ mb: 3 }}
        data-testid="caregiver-signup-step-header"
      >
        {CAREGIVER_ONBOARDING_STEPS.map((step) => (
          <Step key={step} completed={completedSet.has(step)}>
            <StepLabel data-testid={`caregiver-signup-step-${step}`}>
              {CAREGIVER_ONBOARDING_STEP_LABELS[step]}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          data-testid="caregiver-signup-stepper-error"
        >
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            {CAREGIVER_ONBOARDING_STEP_LABELS[currentStep]}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {CAREGIVER_ONBOARDING_STEP_HINTS[currentStep]}
          </Typography>

          {currentStep === 'profile_details' && (
            <Box data-testid="caregiver-signup-step-profile_details-body">
              <Typography variant="body1" sx={{ mb: 2 }}>
                Add a short bio and a friendly photo so families can get to
                know you. We'll wait here while you do — your profile lives at
                the link below.
              </Typography>
              <Button
                variant="outlined"
                href="/caregiver-profile-management"
                data-testid="caregiver-signup-open-profile"
              >
                Open my profile to edit
              </Button>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    submitting ||
                    !profile?.bio ||
                    !profile.bio.trim() ||
                    !profile.avatar
                  }
                  onClick={() => advance('profile_details')}
                  data-testid="caregiver-signup-advance-profile_details"
                >
                  My profile is ready
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === 'credentials' && (
            <Box data-testid="caregiver-signup-step-credentials-body">
              <Typography variant="body1" sx={{ mb: 2 }}>
                Share at least one credential — either upload a document or
                enter the state license number you already have.
              </Typography>

              <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 2 }}>
                <TextField
                  label="State license number (optional)"
                  value={licenseInput}
                  onChange={(e) => setLicenseInput(e.target.value)}
                  inputProps={{
                    'data-testid': 'caregiver-signup-license-input',
                    maxLength: 64,
                  }}
                  sx={{ minWidth: 240 }}
                />
                <Button
                  variant="outlined"
                  onClick={saveLicense}
                  disabled={
                    licenseSaving ||
                    !licenseInput.trim() ||
                    licenseInput.trim() === (profile?.stateLicenseNumber ?? '')
                  }
                  data-testid="caregiver-signup-save-license"
                >
                  {licenseSaving ? 'Saving…' : 'Save license number'}
                </Button>
              </Stack>

              <StateAgencyRegisterLink
                stateCode={profile?.stateCode ?? null}
                licenseNumber={profile?.stateLicenseNumber ?? null}
                surface="signup"
              />

              <Divider sx={{ my: 3 }} />

              <CredentialDocumentUploader
                onUploaded={async () => {
                  await refreshProgress();
                }}
              />

              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  onClick={() => advance('credentials')}
                  data-testid="caregiver-signup-advance-credentials"
                >
                  Continue
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === 'learning' && (
            <Box data-testid="caregiver-signup-step-learning-body">
              <Typography variant="body1" sx={{ mb: 2 }}>
                Watch a short module and confirm you're comfortable with what
                was shared. We'll record your acknowledgment in your learning
                record.
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                {REQUIRED_MODULE_IDS.map((moduleId) => (
                  <ModuleAcknowledgmentGate
                    key={moduleId}
                    moduleId={moduleId}
                    contentVersion={1}
                    onAcknowledged={async () => {
                      await refreshProgress();
                    }}
                  />
                ))}
              </Stack>
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={() => advance('learning')}
                data-testid="caregiver-signup-advance-learning"
              >
                Continue
              </Button>
            </Box>
          )}

          {currentStep === 'platform_rules' && (
            <Box data-testid="caregiver-signup-step-platform_rules-body">
              <Typography variant="h6" component="h3" gutterBottom>
                {PLATFORM_RULES_TITLE}
              </Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {PLATFORM_RULES_BULLETS.map((bullet) => (
                  <Stack
                    key={bullet}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <CheckCircleIcon
                      color="primary"
                      fontSize="small"
                      sx={{ mt: 0.25 }}
                      aria-hidden="true"
                    />
                    <Typography variant="body1">{bullet}</Typography>
                  </Stack>
                ))}
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rulesChecked}
                    onChange={(e) => setRulesChecked(e.target.checked)}
                    inputProps={
                      {
                        'aria-label': PLATFORM_RULES_CONFIRM,
                        'data-testid': 'caregiver-signup-rules-checkbox',
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label={PLATFORM_RULES_CONFIRM}
              />
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={submitting || !rulesChecked}
                  onClick={() => advance('platform_rules')}
                  data-testid="caregiver-signup-advance-platform_rules"
                >
                  Continue
                </Button>
              </Box>
            </Box>
          )}

          {currentStep === 'disclaimer' && (
            <Box data-testid="caregiver-signup-step-disclaimer-body">
              <Typography variant="h6" component="h3" gutterBottom>
                {DISCLAIMER_TITLE}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {DISCLAIMER_BODY}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={disclaimerChecked}
                    onChange={(e) => setDisclaimerChecked(e.target.checked)}
                    inputProps={
                      {
                        'aria-label': DISCLAIMER_CONFIRM,
                        'data-testid': 'caregiver-signup-disclaimer-checkbox',
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label={DISCLAIMER_CONFIRM}
              />
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={submitting || !disclaimerChecked}
                  onClick={() => advance('disclaimer')}
                  data-testid="caregiver-signup-advance-disclaimer"
                >
                  I'm all set
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="text"
          color="primary"
          onClick={onExit}
          data-testid="caregiver-signup-save-and-continue-later"
        >
          Save and continue later
        </Button>
        {activeStepIdx > 0 && (
          <Button
            variant="text"
            color="primary"
            onClick={() => setActiveStepIdx((idx) => Math.max(0, idx - 1))}
            data-testid="caregiver-signup-back"
          >
            Back
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CaregiverSignUpStepper;
